<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

import AppIcon from '../components/AppIcon.vue'
import SiteFooter from '../components/SiteFooter.vue'
import SiteHeader from '../components/SiteHeader.vue'
import { ApiError, apiRequest } from '../services/api'

interface Status { installed: boolean; schemaVersion: number; schemaCompatible: boolean; applicationVersion: string; environment: string; checks: Record<string, boolean> }

const token = ref('')
const name = ref('')
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const showPassword = ref(false)
const status = ref<Status | null>(null)
const error = ref('')
const busy = ref(false)
const maintenanceResult = ref<Record<string, unknown> | null>(null)
const readyToCreate = computed(() => status.value && !status.value.installed)

async function inspect(): Promise<void> {
  error.value = ''
  busy.value = true
  try {
    status.value = await apiRequest<Status>('/api/install/status', {
      method: 'POST',
      body: JSON.stringify({ token: token.value }),
    })
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Unable to check installation status.'
  } finally {
    busy.value = false
  }
}

async function initialize(): Promise<void> {
  error.value = ''
  busy.value = true
  try {
    await apiRequest('/api/install/initialize', {
      method: 'POST',
      body: JSON.stringify({
        token: token.value,
        name: name.value,
        email: email.value,
        password: password.value,
        passwordConfirmation: passwordConfirmation.value,
      }),
    })
    status.value = { ...(status.value as Status), installed: true }
    token.value = ''
    password.value = ''
    passwordConfirmation.value = ''
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Installation could not be completed.'
  } finally {
    busy.value = false
  }
}

async function maintain(operation: 'HEALTH' | 'INTEGRITY' | 'CLEAN_EXPIRED'): Promise<void> {
  error.value = ''
  busy.value = true
  try {
    maintenanceResult.value = await apiRequest<Record<string, unknown>>('/api/install/maintenance', {
      method: 'POST', body: JSON.stringify({ token: token.value, operation }),
    })
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Maintenance operation failed.'
  } finally { busy.value = false }
}

function lockConsole(): void {
  token.value = ''
  status.value = null
  maintenanceResult.value = null
}
</script>

<template>
  <div
    class="page-wrapper authentication-bg"
    :style="{ backgroundImage: 'url(/images/auth-bg.jpg)' }"
  >
    <SiteHeader minimal />
    <main class="section section--top">
      <div class="container">
        <div class="grid grid--split">
          <section
            v-reveal
            class="authentication__inner"
            aria-labelledby="install-title"
          >
            <RouterLink
              to="/"
              class="btn--secondary"
            >
              <AppIcon name="arrow-left" /> Back to home
            </RouterLink>
            <div class="mt-40">
              <span class="sub-title"><strong>Protected</strong> setup</span>
              <h4
                id="install-title"
                class="fw-6 mt-12"
              >
                Install
              </h4>
              <p class="mt-12 muted-text">
                Unlock setup and maintenance with the Install Token configured for this deployment.
              </p>
            </div>

            <p
              v-if="error"
              class="alert mt-24"
              role="alert"
            >
              {{ error }}
            </p>

            <form
              v-if="!status"
              class="stack stack--lg mt-30"
              @submit.prevent="inspect"
            >
              <div class="input-wrapper">
                <label for="install-token">Install Token</label>
                <div class="input-single">
                  <AppIcon name="lock" />
                  <input
                    id="install-token"
                    v-model="token"
                    type="password"
                    autocomplete="off"
                    placeholder="Paste the Install Token"
                    required
                  >
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  class="btn--primary"
                  :disabled="busy"
                >
                  {{ busy ? 'Checking…' : 'Continue' }} <AppIcon name="arrow-right" />
                </button>
              </div>
            </form>

            <div
              v-else-if="status.installed"
              class="stack stack--lg mt-30"
            >
              <div class="panel">
                <h2 class="text-xxl fw-6">
                  Maintenance console
                </h2>
                <p class="mt-8">
                  Installation is complete.
                </p>
                <p
                  v-if="status.environment"
                  class="mt-8"
                >
                  Environment: <span class="primary-text fw-6">{{ status.environment }}</span>
                </p>
                <p class="status-note mt-8">
                  Schema {{ status.schemaVersion }} {{ status.schemaCompatible ? 'compatible' : 'mismatch' }} · App {{ status.applicationVersion }}
                </p>
                <ul class="check-list mt-16">
                  <li
                    v-for="(available, check) in status.checks"
                    :key="check"
                    class="row-flex"
                  >
                    <AppIcon
                      :name="available ? 'circle-check' : 'x'"
                      :size="20"
                      :class="available ? 'primary-text' : 'secondary-text'"
                    /> {{ check }}
                  </li>
                </ul>
              </div>
              <div class="button-row">
                <button
                  type="button"
                  class="btn--secondary"
                  :disabled="busy"
                  @click="maintain('HEALTH')"
                >
                  Run health diagnostics
                </button>
                <button
                  type="button"
                  class="btn--secondary"
                  :disabled="busy"
                  @click="maintain('INTEGRITY')"
                >
                  Run integrity checks
                </button>
                <button
                  type="button"
                  class="btn--secondary"
                  :disabled="busy"
                  @click="maintain('CLEAN_EXPIRED')"
                >
                  Clean expired sessions
                </button>
              </div>
              <pre
                v-if="maintenanceResult"
                class="maintenance-result"
              >{{ JSON.stringify(maintenanceResult, null, 2) }}</pre>
              <div class="button-row">
                <RouterLink
                  class="btn--primary"
                  to="/admin"
                >
                  Admin login <AppIcon name="arrow-right" />
                </RouterLink>
                <button
                  type="button"
                  class="btn--danger"
                  @click="lockConsole"
                >
                  Lock maintenance console
                </button>
              </div>
            </div>

            <form
              v-else-if="readyToCreate"
              class="stack stack--lg mt-30"
              @submit.prevent="initialize"
            >
              <p>Create the first administrator. Initial setup locks after this succeeds.</p>
              <div class="input-wrapper">
                <label for="admin-name">Name</label>
                <div class="input-single">
                  <AppIcon name="user" />
                  <input
                    id="admin-name"
                    v-model="name"
                    autocomplete="name"
                    minlength="2"
                    maxlength="80"
                    required
                  >
                </div>
              </div>
              <div class="input-wrapper">
                <label for="admin-email">Email</label>
                <div class="input-single">
                  <AppIcon name="mail" />
                  <input
                    id="admin-email"
                    v-model="email"
                    type="email"
                    autocomplete="email"
                    required
                  >
                </div>
              </div>
              <div class="input-wrapper password-group">
                <label for="admin-password">Password</label>
                <div class="input-single">
                  <AppIcon name="lock" />
                  <input
                    id="admin-password"
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    minlength="12"
                    required
                  >
                  <button
                    type="button"
                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    @click="showPassword = !showPassword"
                  >
                    <AppIcon :name="showPassword ? 'eye-off' : 'eye'" />
                  </button>
                </div>
              </div>
              <div class="input-wrapper">
                <label for="password-confirmation">Confirm password</label>
                <div class="input-single">
                  <AppIcon name="lock" />
                  <input
                    id="password-confirmation"
                    v-model="passwordConfirmation"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    required
                  >
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  class="btn--primary"
                  :disabled="busy"
                >
                  {{ busy ? 'Installing…' : 'Create administrator' }} <AppIcon name="arrow-right" />
                </button>
              </div>
            </form>
          </section>
          <div
            v-reveal
            class="text-center"
            aria-hidden="true"
          >
            <img
              src="/images/winner.png"
              alt=""
              style="max-width: min(100%, 26rem); opacity: .9"
            >
          </div>
        </div>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
